import React, { useState } from 'react'
import PropTypes from 'prop-types'

import MrModal from '@hixme-ui/modal'
import Button from '@hixme-ui/button'
import Text from '@hixme-ui/text'
import UploadBox from '@hixme-ui/upload-box'
import { getEnrollmentApiErrorMessage } from '../utils'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ACCEPT = '.pdf,.jpg,.jpeg,.png'

const modalStyles = {
  title: { fontSize: 18, fontWeight: 800, color: '#334155', marginBottom: 8, textAlign: 'left' },
  subtitle: { fontSize: 14, color: '#64748b', marginBottom: 24, textAlign: 'left' },
  sectionTitle: { fontSize: 13, fontWeight: 800, marginBottom: 6, color: '#334155' },
  addressBlock: { fontSize: 14, color: '#475569', marginBottom: 16, lineHeight: 1.5 },
  alertBox: {
    background: '#fefce8',
    borderLeft: '4px solid #eab308',
    padding: '12px 14px',
    marginBottom: 20,
    fontSize: 14,
    color: '#475569',
  },
  needsMoreInfoNote: {
    fontSize: 13,
    color: '#92400e',
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 16,
    lineHeight: 1.45,
  },
  uploadsLockedNote: {
    fontSize: 13,
    color: '#1e40af',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 16,
    lineHeight: 1.45,
  },
  uploadHeading: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.5,
    marginBottom: 6,
    color: '#334155',
  },
  uploadHint: { fontSize: 13, color: '#64748b', marginBottom: 12 },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 24,
    paddingTop: 20,
    borderTop: '1px solid #e5e7eb',
  },
  supportLink: {
    color: '#00a0a8',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  downloadLink: {
    color: '#00a0a8',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 0,
    padding: 0,
    fontSize: 'inherit',
  },
  uploadedDocRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px solid #f1f5f9',
    fontSize: 14,
    color: '#475569',
  },
}

function formatResidentialAddress(person) {
  if (!person) return ''
  const parts = [
    person.StreetAddress,
    person.StreetAddressExt,
    [person.City, person.StateProvince, person.PostalCode].filter(Boolean).join(', '),
  ].filter(Boolean)
  return parts.join('\n')
}

function formatMailingAddress(person) {
  if (!person) return 'Same as residential'
  const hasMailing =
    person.MailingStreetAddress ||
    person.MailingCity ||
    person.MailingStateProvince ||
    person.MailingPostalCode
  if (!hasMailing) return 'Same as residential'
  const parts = [
    person.MailingStreetAddress,
    person.MailingStreetAddressExt,
    [person.MailingCity, person.MailingStateProvince, person.MailingPostalCode]
      .filter(Boolean)
      .join(', '),
  ].filter(Boolean)
  return parts.join('\n')
}

const ProvideDocumentModal = ({
  title,
  person,
  onClose,
  onSubmit,
  category,
  planNameSnapshot,
  carrierNameSnapshot,
  requestForPeopleNames,
  uploadedDocuments,
  onDownloadDocument,
  isReplace,
  needsMoreDocumentsNotice,
  uploadsNotAllowedReason,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const handleDrop = (acceptedFiles) => {
    if (uploadsNotAllowedReason) return
    if (acceptedFiles && acceptedFiles.length > 0) {
      setSelectedFiles(
        isReplace ? acceptedFiles.slice(0, 1) : (prev) => [...prev, ...acceptedFiles]
      )
      setError(null)
    }
  }

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
    setError(null)
  }

  const handleSubmit = () => {
    if (uploadsNotAllowedReason || !selectedFiles.length) return
    setError(null)
    setSubmitting(true)
    onSubmit(selectedFiles)
      .catch((err) => {
        setError(getEnrollmentApiErrorMessage(err))
      })
      .finally(() => setSubmitting(false))
  }

  const hasFiles = selectedFiles.length > 0
  const uploadsLocked = Boolean(uploadsNotAllowedReason)
  const showAddress = category === 'PROOF_OF_RESIDENCY'
  const residential = showAddress ? formatResidentialAddress(person) : ''
  const mailing = showAddress ? formatMailingAddress(person) : ''

  return (
    <MrModal medium isOpen handleClose={onClose} contentPadding="24px 24px 24px 24px">
      <h2 style={modalStyles.title}>{title}</h2>
      {uploadsNotAllowedReason && (
        <p style={modalStyles.uploadsLockedNote} role="status">
          {uploadsNotAllowedReason}
        </p>
      )}
      {needsMoreDocumentsNotice && (
        <p style={modalStyles.needsMoreInfoNote}>
          We need a bit more documentation to finish reviewing your request. Please upload any
          additional files requested below.
        </p>
      )}
      {(planNameSnapshot ||
        carrierNameSnapshot ||
        (requestForPeopleNames && requestForPeopleNames.length > 0)) && (
        <div style={{ marginBottom: 16, fontSize: 14, color: '#475569' }}>
          {requestForPeopleNames && requestForPeopleNames.length > 0 && (
            <div style={{ marginBottom: 4 }}>
              <strong>Request for:</strong> {requestForPeopleNames.join(', ')}
            </div>
          )}
          {planNameSnapshot && (
            <div style={{ marginBottom: 4 }}>
              <strong>Plan:</strong> {planNameSnapshot}
            </div>
          )}
          {carrierNameSnapshot && (
            <div>
              <strong>Carrier:</strong> {carrierNameSnapshot}
            </div>
          )}
        </div>
      )}
      {uploadedDocuments && uploadedDocuments.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={modalStyles.sectionTitle}>Uploaded documents</div>
          <div style={{ fontSize: 14, color: '#475569' }}>
            {uploadedDocuments.map((doc, index) => {
              const uploadId = doc.documentUploadId ?? doc.id
              const fileName = doc.originalFileName ?? 'Document'
              return (
                <div key={uploadId ?? index} style={modalStyles.uploadedDocRow}>
                  {uploadId && onDownloadDocument ? (
                    <button
                      type="button"
                      style={modalStyles.downloadLink}
                      onClick={() => onDownloadDocument(uploadId)}
                    >
                      {fileName}
                    </button>
                  ) : (
                    <span>{fileName}</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
      <p style={modalStyles.subtitle}>
        {showAddress
          ? 'Enter your address and upload verification document'
          : 'Upload your verification document'}
      </p>

      {showAddress && (
        <>
          <div style={modalStyles.sectionTitle}>Residential Address</div>
          <div style={modalStyles.addressBlock}>{residential || '—'}</div>

          <div style={modalStyles.sectionTitle}>Mailing Address</div>
          <div style={modalStyles.addressBlock}>{mailing}</div>

          <div style={modalStyles.alertBox}>
            If there are any errors in the address shown, get in touch with our{' '}
            <span
              style={modalStyles.supportLink}
              role="button"
              tabIndex={0}
              onClick={() => {
                window.location.href = 'mailto:support@sureco.com'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') window.location.href = 'mailto:support@sureco.com'
              }}
            >
              Support Team
            </span>{' '}
            right away.
          </div>
        </>
      )}

      <div style={modalStyles.uploadHeading}>UPLOAD DOCUMENTATION</div>
      <div style={modalStyles.uploadHint}>
        {uploadsLocked
          ? 'Uploads are not available for completed requests.'
          : showAddress
          ? 'Upload a utility bill, bank statement, or official document showing your name and address.'
          : 'Upload your document (PDF, JPG, or PNG, max 10MB).'}
      </div>
      <div
        style={
          uploadsLocked
            ? { opacity: 0.55, pointerEvents: 'none', marginBottom: 8 }
            : { marginBottom: 8 }
        }
      >
        <UploadBox
          onDrop={handleDrop}
          accept={ACCEPT}
          maxSize={MAX_FILE_SIZE}
          multiple={!isReplace}
        />
      </div>
      {selectedFiles.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Text small style={{ color: '#64748b', marginBottom: 4 }}>
            Selected ({selectedFiles.length}):{' '}
          </Text>
          {selectedFiles.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '4px 8px',
                background: '#f1f5f9',
                borderRadius: 6,
                marginTop: 4,
                fontSize: 13,
              }}
            >
              <span style={{ color: '#334155' }}>{file.name}</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                style={{
                  background: 'none',
                  border: 0,
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: 12,
                  padding: '0 4px',
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
      {error && (
        <Text small style={{ marginTop: 8, color: '#ef4444' }}>
          {error}
        </Text>
      )}

      <div style={modalStyles.footer}>
        <Button outline onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!hasFiles || uploadsLocked}
          submitting={submitting}
          submittingText="Submitting..."
          style={{ background: '#00a0a8', color: 'white', border: 'none' }}
        >
          Submit
        </Button>
      </div>
    </MrModal>
  )
}

ProvideDocumentModal.propTypes = {
  title: PropTypes.string.isRequired,
  person: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  category: PropTypes.string,
  planNameSnapshot: PropTypes.string,
  carrierNameSnapshot: PropTypes.string,
  requestForPeopleNames: PropTypes.arrayOf(PropTypes.string),
  uploadedDocuments: PropTypes.arrayOf(PropTypes.object),
  onDownloadDocument: PropTypes.func,
  isReplace: PropTypes.bool,
  needsMoreDocumentsNotice: PropTypes.bool,
  uploadsNotAllowedReason: PropTypes.string,
}

ProvideDocumentModal.defaultProps = {
  person: null,
  category: null,
  planNameSnapshot: null,
  carrierNameSnapshot: null,
  requestForPeopleNames: [],
  uploadedDocuments: [],
  onDownloadDocument: null,
  isReplace: false,
  needsMoreDocumentsNotice: false,
  uploadsNotAllowedReason: null,
}

export default ProvideDocumentModal
